import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  useTheme,
  InputBase,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  CloseRounded,
  DeleteRounded,
  ErrorOutlineOutlined,
  NotesRounded,
  ThumbDownAltRounded,
  ThumbDownOffAltRounded,
  ThumbUpAltRounded,
  ThumbUpOffAlt,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import HighlightText from "./HighlightText";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteFeedback,
  recordFeeback,
} from "../../../../../core/repo/sessionRepo";
import { usePostHog } from "posthog-js/react";
import {
  DELETE_FEEDBACK_FAILURE,
  DELETE_FEEDBACK_SUCCESS,
  OPEN_FEEDBACK_DIALOG,
  SUBMIT_FEEDBACK_FAILURE,
  SUBMIT_FEEDBACK_SUCCESS,
} from "../../../../../utils/posthogEvents";
import { GradientIconAccent } from "../../../../../components/GradientIcon";
import FeedbackChips from "../../../../../components/FeedbackChips";
import StreamBlockParser from "../../../../../utils/StreamBlockParser";
import { useThemeSettings } from "../../../../../theme/ThemeSettingsProvider";

// Helper function to extract only visible text (no tool calls, tool results, or thinking blocks)
const extractVisibleText = (rawText) => {
  if (!rawText) return "";

  const blocks = StreamBlockParser.parseComplete(rawText);
  const textBlocks = blocks.filter((block) => block.type === "text");
  return textBlocks.map((block) => block.content).join("\n\n");
};

export const useStyles = makeStyles()((theme, _params, classes) => ({
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  icon: {
    cursor: "pointer",
    transition: "all 0.1s linear",
    ":hover": {
      fontSize: 30,
    },
  },
}));

function Feedback({ message }) {
  const theme = useTheme();
  const posthog = usePostHog();
  const { enqueueSnackbar } = useSnackbar();
  const [query, setQuery] = useState(message["query"]);
  const answerFeedback = query["answer_feedback"];
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { selectedDomain } = useSelector((state) => state.domain);
  const dispatch = useDispatch();

  const handleOpen = () => {
    setIsDialogOpen(true);
    posthog.capture(OPEN_FEEDBACK_DIALOG);
  };

  const submitFeedback = async (
    rating,
    highlightedSections,
    description,
    feedbackType,
    feedbackChips
  ) => {
    setIsLoading(true);
    const feedbackData = {
      message_id: query["message_id"],
      rating,
      highlighted_sections: highlightedSections,
      description,
      partial:
        highlightedSections.length > 0 &&
        highlightedSections[0].text !==
          message["answer"]["content_object"]["text"],
      feedback: feedbackChips.toString(),
      domain_id: selectedDomain?.id,
    };
    try {
      await dispatch(recordFeeback(query["message_id"], feedbackData));
      setQuery({ ...query, answer_feedback: feedbackData });
      enqueueSnackbar("Your feedback has been recorded", {
        variant: "success",
      });
      posthog.capture(SUBMIT_FEEDBACK_SUCCESS, { rating: rating });
      setIsDialogOpen(false);
    } catch (err) {
      console.log(err);
      enqueueSnackbar("Failed to record your feedback", {
        variant: "error",
      });
      posthog.capture(SUBMIT_FEEDBACK_FAILURE);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAnswerFeedback = async () => {
    try {
      setIsLoading(true);
      await dispatch(deleteFeedback(query["message_id"]));
      setQuery({ ...query, answer_feedback: null });
      enqueueSnackbar("Your feedback has been deleted", {
        variant: "success",
      });
      posthog.capture(DELETE_FEEDBACK_SUCCESS);
      setIsDialogOpen(false);
    } catch (err) {
      enqueueSnackbar("Failed to delete the feedback", {
        variant: "error",
      });
      posthog.capture(DELETE_FEEDBACK_FAILURE);
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <CircularProgress
          size={18}
          sx={{ color: theme.palette.action.active }}
        />
      ) : (
        <Box display="flex" alignItems="center">
          <Tooltip title="Good Answer">
            <IconButton
              onClick={() => {
                if (answerFeedback) {
                  deleteAnswerFeedback();
                } else {
                  submitFeedback("good", [], "", "good", []);
                }
              }}
              size="small"
              sx={{ color: theme.palette.action.active, marginRight: 1 }}>
              {answerFeedback && answerFeedback["rating"] === "good" ? (
                <GradientIconAccent
                  Icon={ThumbUpAltRounded}
                  sx={{ fontSize: 20 }}
                />
              ) : (
                <ThumbUpOffAlt fontSize="small" sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Answer not up to the mark">
            <IconButton
              onClick={handleOpen}
              size="small"
              sx={{ color: theme.palette.action.active }}>
              {answerFeedback && answerFeedback["rating"] === "bad" ? (
                <GradientIconAccent
                  Icon={ThumbDownAltRounded}
                  sx={{ fontSize: 20 }}
                />
              ) : (
                <ThumbDownOffAltRounded
                  fontSize="small"
                  sx={{ fontSize: 18 }}
                />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {isDialogOpen && (
        <FeedbackDialog
          message={message}
          onClose={() => setIsDialogOpen(false)}
          submitFeedback={submitFeedback}
          deleteAnswerFeedback={deleteAnswerFeedback}
        />
      )}
    </>
  );
}

function FeedbackDialog({
  message,
  onClose,
  submitFeedback,
  deleteAnswerFeedback,
}) {
  const { mode } = useThemeSettings();
  const isDarkMode = mode === "dark";
  const { query, answer } = message;
  const answerFeedback = query["answer_feedback"];
  const { classes } = useStyles();
  const theme = useTheme();
  const [rating, setRating] = useState("bad");
  const [additionalFeedback, setAdditionalFeedback] = useState(
    answerFeedback ? answerFeedback["description"] : ""
  );

  let stateValue = [];
  if (
    answerFeedback &&
    answerFeedback["feedback"] &&
    answerFeedback["feedback"].length !== 0
  ) {
    stateValue = answerFeedback["feedback"].split(",");
  }
  const [feedbackChips, setFeedbackChips] = useState(stateValue);
  const [isLoading, setIsLoading] = useState(false);
  const editorRef = useRef();
  const [isEntityAnswer, setIsEntityAnswer] = useState(false);

  // Extract visible text (filtering out tool calls, tool results, thinking blocks)
  const visibleText = extractVisibleText(answer["content_object"]["text"]);

  useEffect(() => {
    const entityAnswer = answer["content_object"]["type"] === "entity";
    setIsEntityAnswer(entityAnswer);
  }, [answer]);

  const handleClickSubmitFeedback = () => {
    const highlightedSections = getHighlightedText();
    submitFeedback(
      rating,
      highlightedSections.length > 0
        ? highlightedSections
        : [{ text: visibleText }],
      additionalFeedback,
      "feedback", // simplified type
      feedbackChips
    );
  };

  const handleClickFeedbackChip = (feedback) => {
    const isExists = feedbackChips.find((c) => c === feedback);
    let tempFeedbackChips = [...feedbackChips];
    if (!isExists) {
      tempFeedbackChips = [...tempFeedbackChips, feedback];
    } else {
      tempFeedbackChips = tempFeedbackChips.filter((c) => c !== feedback);
    }
    setFeedbackChips(tempFeedbackChips);
  };

  const getHighlightedText = () => {
    let formattedTextArr = [];
    if (editorRef.current) {
      const quill = editorRef.current.getEditor();
      const contents = quill.getContents();

      contents.ops.forEach((op) => {
        if (op.attributes && op.attributes.background) {
          formattedTextArr = [...formattedTextArr, { text: op.insert }];
        }
      });
    }
    return formattedTextArr;
  };

  const clearSelection = () => {
    const quill = editorRef.current.getEditor();
    if (quill) {
      quill.formatText(0, quill.getLength(), "background", false);
    }
    setFeedbackChips([]);
    setAdditionalFeedback("");
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <Typography variant="h6">What's the issue with Answer?</Typography>
        <Box display={"flex"} alignItems={"center"}>
          {answerFeedback && (
            <Tooltip title={"Remove Feedback"}>
              <IconButton onClick={deleteAnswerFeedback} sx={{ mr: 1 }}>
                <DeleteRounded />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: "500px" }}>
        <Box sx={{ display: "flex", height: "100%" }}>
          {!isEntityAnswer && (
            <Box
              sx={{
                width: "50%",
                height: "100%",
                p: 3,
                borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                display: "flex",
                flexDirection: "column",
              }}>
              <Box mb={1} display={"flex"} alignItems={"center"}>
                <NotesRounded sx={{ color: theme.palette.action.disabled }} />
                <Box mr={1} />
                <Typography variant="subtitle2" color={"textSecondary"}>
                  Highlight specific parts of the answer (optional)
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: theme.palette.background.light,
                  borderRadius: 3,
                  p: 2,
                  flex: 1,
                  "& .ql-editor": {
                    fontSize: theme.typography.caption.fontSize,
                    lineHeight: theme.typography.caption.lineHeight,
                    fontFamily: theme.typography.caption.fontFamily,
                    fontWeight: theme.typography.caption.fontWeight,
                    letterSpacing: theme.typography.caption.letterSpacing,
                  },
                }}>
                <HighlightText
                  editorRef={editorRef}
                  message={visibleText}
                  typeOfAnswer="part_of_answer"
                />
              </Box>
            </Box>
          )}
          <Box
            sx={{
              width: isEntityAnswer ? "100%" : "50%",
              height: "100%",
              p: 3,
              display: "flex",
              flexDirection: "column",
            }}>
            <Box
              mb={1}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}>
              <Box display={"flex"} alignItems={"center"}>
                <ErrorOutlineOutlined
                  sx={{ color: theme.palette.action.disabled }}
                />
                <Box mr={1} />
                <Typography variant="subtitle2" color={"textSecondary"}>
                  Select the issue(s)
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                onClick={clearSelection}
                disabled={feedbackChips.length === 0 && !additionalFeedback}
                sx={{ minWidth: "auto", p: 1 }}>
                Clear
              </Button>
            </Box>
            <Box
              flex={1}
              display={"flex"}
              flexDirection={"column"}
              bgcolor={theme.palette.background.default}
              borderRadius={3}
              p={2}
              sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <FeedbackChips
                chips={feedbackChips}
                readonly={false}
                onChipClick={handleClickFeedbackChip}
              />
              <InputBase
                fullWidth
                multiline
                placeholder="Add additional feedback"
                value={additionalFeedback}
                onChange={(e) => setAdditionalFeedback(e.target.value)}
                sx={{
                  flex: 1,
                  minHeight: "100px",
                  overflow: "auto",
                  alignItems: "start",
                  padding: 2,
                  borderRadius: 2,
                  border: isDarkMode
                    ? `1px solid ${theme.palette.primary.main}`
                    : "1px solid rgba(0, 0, 0, 0.23)",
                  background: isDarkMode
                    ? theme.palette.background.light
                    : "#f9f9f9",
                  fontSize: 14,
                  marginTop: 2,
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      {rating && (
        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button
            onClick={handleClickSubmitFeedback}
            variant="contained"
            color="primary"
            disabled={feedbackChips.length === 0}>
            Submit Feedback
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default Feedback;
